import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, User, Users, Shield, Settings, Store, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Panel, MonoLabel } from "@/components/Panel";
import { otpApi, staffLogin, authLookup, forgotApi, MEMBER_TOKEN_KEY, ADMIN_TOKEN_KEY, apiErr } from "@/lib/authApi";
import { OtpInput } from "@/components/OtpInput";
import { TOKEN_KEY as DEVADMIN_TOKEN_KEY } from "@/lib/devadminApi";
import { verifyLeaderPin, startLeaderSession } from "@/lib/referralsApi";
import { EVENT_SLUG } from "@/lib/publicApi";

const ICONS = { member: User, leader: Users, admin: Shield, devadmin: Settings, stall_manager: Store };
const DEST = { member: "/member", leader: "/leader", admin: "/admin", devadmin: "/devadmin", stall_manager: "/admin/registrations" };
const EMPTY = ["", "", "", "", "", ""];

export function LoginModal({ open, onClose }) {
  const nav = useNavigate();
  const [step, setStep] = useState("phone"); // phone | otp | password | pin | forgot-otp | reset
  const [phone, setPhone] = useState("");
  const [roles, setRoles] = useState([]);
  const [role, setRole] = useState(null);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pin, setPin] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState(EMPTY);
  const [reqId, setReqId] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const otpRefs = useRef([]);

  useEffect(() => {
    if (open) {
      setStep("phone"); setPhone(""); setRoles([]); setRole(null); setPassword(""); setNewPw(""); setConfirmPw("");
      setPin(""); setName(""); setOtp(EMPTY); setReqId(null); setResetToken(null); setErr(""); setBusy(false);
    }
  }, [open]);

  const digits = phone.replace(/\D/g, "").slice(-10);
  const e164 = `+91${digits}`;
  const focusOtp = () => setTimeout(() => otpRefs.current[0]?.focus(), 100);
  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(apiErr(e)); } finally { setBusy(false); } };

  const enterRole = (r) => {
    setRole(r); setErr(""); setOtp(EMPTY); setPassword("");
    if (r.method === "otp") { setStep("otp"); sendLoginOtp(); }
    else setStep(r.method); // password | pin
  };

  const lookup = () => {
    if (digits.length < 10) return setErr("Enter a valid 10-digit mobile number");
    run(async () => {
      const r = await authLookup(e164);
      setRoles(r.roles);
      if (r.roles.length > 1) setStep("choose"); else enterRole(r.roles[0]);
    });
  };

  const sendLoginOtp = () => run(async () => {
    const r = await otpApi.request(e164, EVENT_SLUG);
    setReqId(r.request_id); focusOtp();
    toast.success(`OTP sent to +91 ${digits.slice(0, 5)} ${digits.slice(5)}`);
  });

  const finish = (key, token, label) => {
    localStorage.setItem(key, token);
    toast.success(`Signed in · Opening ${label}`);
    onClose?.(); setTimeout(() => nav(DEST[role.role]), 150);
  };

  const verifyLoginOtp = (code) => run(async () => {
    const r = await otpApi.verify(e164, code, reqId);
    finish(MEMBER_TOKEN_KEY, r.access_token || r.token, "Member Portal");
  });

  const passwordSignIn = () => {
    if (!password) return setErr("Password required");
    run(async () => {
      const r = await staffLogin(digits, password, role.role);
      const key = role.role === "devadmin" ? DEVADMIN_TOKEN_KEY : ADMIN_TOKEN_KEY;
      if (role.role === "leader") {
        let session_id = null;
        try { session_id = (await startLeaderSession({ name: r.user?.name || "", phone: digits })).id; } catch {}
        localStorage.setItem("bni-leader", JSON.stringify({ name: r.user?.name || "Leader", phone: digits, session_id }));
        finish(ADMIN_TOKEN_KEY, r.token, "Leader Console");
      } else finish(key, r.access_token || r.token, role.label);
    });
  };

  const pinSignIn = () => {
    if (!name.trim()) return setErr("Your name is required");
    if (pin.trim().length < 4) return setErr("Enter the Leader PIN");
    run(async () => {
      const r = await verifyLeaderPin({ pin: pin.trim(), name: name.trim(), phone: digits });
      let session_id = null;
      try { session_id = (await startLeaderSession({ name: r.leader_name, phone: r.leader_phone })).id; } catch {}
      localStorage.setItem("bni-leader", JSON.stringify({ name: r.leader_name, phone: r.leader_phone, session_id }));
      if (r.warning) toast.info(r.warning);
      toast.success("Signed in · Opening Leader Console");
      onClose?.(); setTimeout(() => nav("/leader"), 150);
    });
  };

  const startForgot = () => run(async () => {
    const r = await forgotApi.request(e164, role.role);
    setReqId(r.request_id); setOtp(EMPTY); setStep("forgot-otp"); focusOtp();
    toast.success("Reset code sent on WhatsApp");
  });

  const verifyForgotOtp = (code) => run(async () => {
    const r = await forgotApi.verify(e164, code, role.role, reqId);
    setResetToken(r.reset_token); setStep("reset");
  });

  const doReset = () => {
    if (newPw.length < 8) return setErr("Password must be at least 8 characters");
    if (newPw !== confirmPw) return setErr("Passwords do not match");
    run(async () => {
      await forgotApi.reset(resetToken, newPw);
      toast.success("Password updated · Sign in with your new password");
      setPassword(""); setNewPw(""); setConfirmPw(""); setStep("password");
    });
  };

  const Err = () => err ? <div data-testid="login-err" className="text-sm mt-3" style={{ color: "var(--red-2)" }}>{err}</div> : null;
  const sub = {
    phone: "Enter your mobile number — we'll detect how you sign in",
    choose: "This number has more than one role — pick one",
    otp: "WhatsApp OTP — no password",
    password: "Phone + password",
    pin: "Your name + the day's Leader PIN",
    "forgot-otp": "Verify the reset code sent on WhatsApp",
    reset: "Choose a new password",
  }[step];

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="login-modal">
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} className="w-full max-w-[520px]" onClick={(e) => e.stopPropagation()}>
            <Panel className="p-6">
              <div className="flex justify-center mb-5" data-testid="login-logo-wrap">
                <img src="/risse-bni-dark.webp" alt="RISSE BNI 26 · Davanagere Members Day" className="h-24 w-auto" data-testid="login-logo" />
              </div>
              <div className="flex items-start justify-between">
                <div>
                  <MonoLabel>Sign in</MonoLabel>
                  <div className="font-serif text-2xl mt-2">Members Day · Login</div>
                  <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{sub}</div>
                </div>
                <button data-testid="login-close-btn" onClick={onClose}><X size={18} /></button>
              </div>

              {step !== "phone" && (
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <button type="button" data-testid="login-change-number" onClick={() => { setStep("phone"); setRoles([]); setRole(null); setErr(""); }}
                    className="mono-label" style={{ color: "var(--taupe)" }}>← +91 {digits.slice(0, 5)} {digits.slice(5)} · change</button>
                  {roles.length > 1 && step !== "choose" && (
                    <div className="login-role-toggle" data-testid="login-role-toggle">
                      {roles.map((r) => { const Ic = ICONS[r.role]; return (
                        <button key={r.role} type="button" data-testid={`login-role-${r.role}`} onClick={() => enterRole(r)}
                          className={`login-role-pill ${role?.role === r.role ? "active" : ""}`}><Ic size={12} /> {r.label}</button>
                      ); })}
                    </div>
                  )}
                </div>
              )}

              {step === "phone" && (
                <form onSubmit={(e) => { e.preventDefault(); lookup(); }} className="mt-5">
                  <label className="mono-label" style={{ color: "var(--taupe)" }}>Mobile number</label>
                  <div className="mt-2 flex items-center hairline">
                    <span className="px-3 py-3 text-sm border-r border-[var(--hairline)]" style={{ color: "var(--taupe)" }}>+91</span>
                    <input data-testid="login-phone-input" autoFocus inputMode="numeric" pattern="[0-9]*" value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="98765 43210"
                      className="flex-1 bg-transparent px-3 py-3 text-sm outline-none" style={{ color: "var(--text)" }} />
                  </div>
                  <Err />
                  <button type="submit" data-testid="login-phone-submit" disabled={busy} className="btn-red mt-5 w-full !py-3">
                    {busy ? "Checking…" : <>Continue <ArrowRight size={14} /></>}
                  </button>
                </form>
              )}

              {step === "choose" && (
                <div className="mt-5 grid gap-2" data-testid="login-role-choose">
                  {roles.map((r) => { const Ic = ICONS[r.role]; return (
                    <button key={r.role} type="button" data-testid={`login-choose-${r.role}`} onClick={() => enterRole(r)}
                      className="flex items-center gap-3 hairline px-4 py-3 text-left transition-colors hover:border-[var(--red)]">
                      <span className="w-8 h-8 grid place-items-center" style={{ background: "var(--red)", color: "#fff" }}><Ic size={15} /></span>
                      <span className="flex-1"><div className="text-sm" style={{ color: "var(--text)" }}>{r.label}</div>
                        <div className="mono-label" style={{ color: "var(--taupe)" }}>{r.method === "otp" ? "WhatsApp OTP" : r.method === "pin" ? "Leader PIN" : "Password"}</div></span>
                      <ArrowRight size={14} style={{ color: "var(--taupe)" }} />
                    </button>
                  ); })}
                </div>
              )}

              {(step === "otp" || step === "forgot-otp") && (
                <div className="mt-5">
                  <label className="mono-label" style={{ color: "var(--taupe)" }}>6-digit code · sent on WhatsApp</label>
                  <div className="mt-2">
                    <OtpInput value={otp} onChange={setOtp} testIdPrefix="login-otp" disabled={busy}
                      onComplete={step === "otp" ? verifyLoginOtp : verifyForgotOtp} />
                  </div>
                  <Err />
                  <div className="mt-4 flex items-center justify-end">
                    <button type="button" data-testid="login-otp-resend" onClick={() => { setOtp(EMPTY); step === "otp" ? sendLoginOtp() : startForgot(); }} disabled={busy}
                      className="mono-label" style={{ color: "var(--red-2)" }}>Resend code</button>
                  </div>
                </div>
              )}

              {step === "password" && (
                <form onSubmit={(e) => { e.preventDefault(); passwordSignIn(); }} className="mt-5">
                  <label className="mono-label" style={{ color: "var(--taupe)" }}>Password</label>
                  <div className="mt-2 flex items-center hairline">
                    <input data-testid="login-staff-password" autoFocus type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                      className="flex-1 bg-transparent px-3 py-3 text-sm outline-none" style={{ color: "var(--text)" }} placeholder="••••••••" />
                    <button type="button" data-testid="login-pw-toggle" onClick={() => setShowPw((s) => !s)} className="px-3" style={{ color: "var(--taupe)" }}>{showPw ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                  </div>
                  <Err />
                  <button type="submit" data-testid="login-staff-submit" disabled={busy} className="btn-red mt-5 w-full !py-3">
                    {busy ? "Signing in…" : <>Sign in <ArrowRight size={14} /></>}
                  </button>
                  <button type="button" data-testid="login-forgot-btn" onClick={startForgot} disabled={busy}
                    className="mono-label mt-4 w-full text-center inline-flex items-center justify-center gap-1" style={{ color: "var(--red-2)" }}>
                    <KeyRound size={12} /> Forgot password?
                  </button>
                </form>
              )}

              {step === "pin" && (
                <form onSubmit={(e) => { e.preventDefault(); pinSignIn(); }} className="mt-5 space-y-3">
                  <div>
                    <label className="mono-label" style={{ color: "var(--taupe)" }}>Your name</label>
                    <input data-testid="login-leader-name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
                      className="mt-2 w-full hairline bg-transparent px-3 py-3 text-sm outline-none focus:border-[var(--red)]" style={{ color: "var(--text)" }} placeholder="As on your badge" />
                  </div>
                  <div>
                    <label className="mono-label" style={{ color: "var(--taupe)" }}>Leader PIN</label>
                    <input data-testid="login-leader-pin" type="password" inputMode="numeric" maxLength={8} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                      className="mt-2 w-full hairline bg-transparent px-3 py-3 text-sm outline-none focus:border-[var(--red)] font-mono" style={{ color: "var(--text)" }} placeholder="••••" />
                  </div>
                  <Err />
                  <button type="submit" data-testid="login-leader-submit" disabled={busy} className="btn-red mt-2 w-full !py-3">
                    {busy ? "Verifying…" : <>Enter Leader Console <ArrowRight size={14} /></>}
                  </button>
                </form>
              )}

              {step === "reset" && (
                <form onSubmit={(e) => { e.preventDefault(); doReset(); }} className="mt-5 space-y-3">
                  <div>
                    <label className="mono-label" style={{ color: "var(--taupe)" }}>New password · min 8 characters</label>
                    <input data-testid="reset-new-password" autoFocus type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                      className="mt-2 w-full hairline bg-transparent px-3 py-3 text-sm outline-none focus:border-[var(--red)]" style={{ color: "var(--text)" }} />
                  </div>
                  <div>
                    <label className="mono-label" style={{ color: "var(--taupe)" }}>Confirm password</label>
                    <input data-testid="reset-confirm-password" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                      className="mt-2 w-full hairline bg-transparent px-3 py-3 text-sm outline-none focus:border-[var(--red)]" style={{ color: "var(--text)" }} />
                  </div>
                  <Err />
                  <button type="submit" data-testid="reset-submit" disabled={busy} className="btn-red mt-2 w-full !py-3">
                    {busy ? "Saving…" : <>Set new password <ArrowRight size={14} /></>}
                  </button>
                </form>
              )}
            </Panel>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
